// src/components/layout/Header.js
import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { fadeIn } from '../../styles/animations';

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 101;
  transition: all ${props => props.theme.transitions.medium};
  background-color: ${props => props.theme.colors.light}; // Fond toujours blanc
`;

const HeaderContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  animation: ${fadeIn} 0.8s ease-out forwards;
  
  img {
    height: 100px;
    width: auto;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: ${props => props.mobileMenuOpen ? 'flex' : 'none'};
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 250px;
    background-color: ${props => props.theme.colors.light};
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    z-index: 101;
    padding: ${props => props.theme.spacing.lg};
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: ${props => props.theme.spacing.md};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
    width: 100%;
  }
`;

const NavItem = styled.li`
  position: relative;
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 100%;
    margin-bottom: ${props => props.theme.spacing.md};
  }
`;

const NavItemLink = styled(NavLink)`
  color: ${props => props.theme.colors.textMain}; // Texte toujours sombre
  font-weight: 600;
  text-decoration: none;
  padding: 0.5rem;
  position: relative;
  transition: color ${props => props.theme.transitions.fast};
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transition: width ${props => props.theme.transitions.medium};
  }
  
  &:hover, &.active {
    color: ${props => props.theme.colors.primary}; // Couleur primaire au survol ou active
    
    &:after {
      width: 100%;
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    color: ${props => props.theme.colors.textMain};
    display: block;
    width: 100%;
    padding: ${props => props.theme.spacing.xs} 0;
    
    &:hover, &.active {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.textMain}; // Texte toujours sombre
  z-index: 102;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: block;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const Overlay = styled.div`
  display: ${props => props.show ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 100;
`;

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();

    // Vérifie si on est sur la page d'accueil
    const isHomePage = location.pathname === '/';

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Réinitialise le menu mobile lors du changement de page
    useEffect(() => {
        closeMobileMenu();
    }, [location]);

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);

        // Prevent scrolling when menu is open
        document.body.style.overflow = !mobileMenuOpen ? 'hidden' : 'auto';
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
        document.body.style.overflow = 'auto';
    };

    const navItems = [
        { path: '/', text: 'ACCUEIL' },
        { path: '/entreprise', text: 'ENTREPRISE' },
        { path: '/techniques', text: 'TECHNIQUES' },
        { path: '/realisations', text: 'RÉALISATIONS' },
        { path: '/blog', text: 'BLOG' },
        { path: '/contact', text: 'CONTACT' }
    ];

    return (
        <>
            <HeaderWrapper scrolled={scrolled} isHomePage={isHomePage}>
                <HeaderContainer>
                    <Logo to="/">
                        <img src="/logo_decko_vecto.png" alt="Decko Logo" />
                    </Logo>

                    <MobileMenuButton
                        onClick={toggleMobileMenu}
                        scrolled={scrolled}
                        isHomePage={isHomePage}
                    >
                        {mobileMenuOpen ? (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                        )}
                    </MobileMenuButton>

                    <Nav mobileMenuOpen={mobileMenuOpen}>
                        <NavList>
                            {navItems.map((item, index) => (
                                <NavItem key={item.path} delay={`${0.1 * (index + 1)}s`}>
                                    <NavItemLink
                                        to={item.path}
                                        scrolled={scrolled}
                                        isHomePage={isHomePage}
                                        isMobile={mobileMenuOpen}
                                        onClick={closeMobileMenu}
                                    >
                                        {item.text}
                                    </NavItemLink>
                                </NavItem>
                            ))}
                        </NavList>
                    </Nav>
                </HeaderContainer>
            </HeaderWrapper>

            <Overlay show={mobileMenuOpen} onClick={closeMobileMenu} />
        </>
    );
};

export default Header;